import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { fetchLicenses, createLicense, updateLicense, fetchProducts, fetchCustomers, fetchLicenseRenewals, fetchServers, fetchLogs } from '../api/admin';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';
import { Tooltip, Snackbar, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, TextField } from '@mui/material';
import { RenewLicenseDialog } from '../components/RenewLicenseDialog';
import { LicenseDetailsDrawer } from '../components/LicenseDetailsDrawer';

function CopyButton({ text, onCopy }: { text: string; onCopy: () => void }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            onCopy();
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy text: ', err);
        }
    };

    return (
        <Tooltip title={copied ? "Copied!" : "Copy License Key"}>
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    handleCopy();
                }}
                className="p-1 ml-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors inline-flex items-center justify-center align-middle"
                type="button"
            >
                {copied ? (
                    <CheckIcon className="h-4 w-4 text-green-500" fontSize="small" />
                ) : (
                    <ContentCopyIcon className="h-4 w-4" fontSize="small" />
                )}
            </button>
        </Tooltip>
    );
}

export function LicensesPage() {
    const queryClient = useQueryClient();
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [productFilter, setProductFilter] = useState('');
    const [editingLimitKey, setEditingLimitKey] = useState<string | null>(null);
    const [tempLimit, setTempLimit] = useState<number>(1);
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    const [openMenuLicenseKey, setOpenMenuLicenseKey] = useState<string | null>(null);
    const [selectedLicense, setSelectedLicense] = useState<any>(null);

    // Dialog & Modal States
    const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
    const [licenseToSuspend, setLicenseToSuspend] = useState<any>(null);

    const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
    const [licenseToRevoke, setLicenseToRevoke] = useState<any>(null);

    const [renewDialogOpen, setRenewDialogOpen] = useState(false);
    const [licenseToRenew, setLicenseToRenew] = useState<any>(null);

    const [snackbarMessage, setSnackbarMessage] = useState('License key copied to clipboard');

    const showMessage = (msg: string) => {
        setSnackbarMessage(msg);
        setSnackbarOpen(true);
    };

    // Create License Form Dialog States
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [newProductId, setNewProductId] = useState('');
    const [newCustomerId, setNewCustomerId] = useState('');
    const [newExpiresAt, setNewExpiresAt] = useState('');
    const [newServerLimit, setNewServerLimit] = useState(1);
    const [newGracePeriodDays, setNewGracePeriodDays] = useState(14);
    const [newLicenseFeatures, setNewLicenseFeatures] = useState<Array<{ key: string; type: 'boolean' | 'string' | 'number'; value: string }>>([]);
    const [newlyCreatedKey, setNewlyCreatedKey] = useState('');
    const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false);

    const resetCreateForm = () => {
        setNewProductId('');
        setNewCustomerId('');
        setNewExpiresAt('');
        setNewServerLimit(1);
        setNewGracePeriodDays(14);
        setNewLicenseFeatures([]);
    };

    const handleCloseCreateDialog = () => {
        setCreateDialogOpen(false);
        resetCreateForm();
    };

    const handleProductChange = (productId: string) => {
        setNewProductId(productId);
        const prod = productsData?.products?.find((p: any) => p.productId === productId);
        if (prod && prod.featuresTemplate) {
            const mapped = Object.entries(prod.featuresTemplate).map(([key, val]) => {
                let type: 'boolean' | 'string' | 'number' = 'string';
                if (typeof val === 'boolean') {
                    type = 'boolean';
                } else if (typeof val === 'number') {
                    type = 'number';
                }
                return { key, type, value: String(val) };
            });
            setNewLicenseFeatures(mapped);
        } else {
            setNewLicenseFeatures([]);
        }
    };

    const getFeatureValidationError = () => {
        const keys = newLicenseFeatures.map(f => f.key.trim());
        if (keys.some(k => k === '')) {
            return 'Feature flag keys cannot be empty';
        }
        const duplicates = keys.filter((item, index) => keys.indexOf(item) !== index);
        if (duplicates.length > 0) {
            return `Duplicate feature keys found: ${duplicates.join(', ')}`;
        }
        return null;
    };

    // Debounce search term to prevent rapid Firestore queries
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 300);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    // Queries
    const { data: licensesData, isLoading: isLoadingLicenses } = useQuery({
        queryKey: ['licenses', debouncedSearch, statusFilter, productFilter],
        queryFn: () => fetchLicenses({
            search: debouncedSearch || undefined,
            status: statusFilter || undefined,
            productId: productFilter || undefined,
        }),
        placeholderData: keepPreviousData,
    });

    const { data: productsData } = useQuery({
        queryKey: ['products'],
        queryFn: fetchProducts,
    });

    const { data: customersData } = useQuery({
        queryKey: ['customers'],
        queryFn: fetchCustomers,
    });

    // Fetch all servers to list/filter inside detailed drawer
    const { data: serversData } = useQuery({
        queryKey: ['servers'],
        queryFn: fetchServers,
    });
    const servers = serversData?.servers || [];


    // Mutations
    const createMutation = useMutation({
        mutationFn: createLicense,
        onSuccess: (resData: any) => {
            queryClient.invalidateQueries({ queryKey: ['licenses'] });
            queryClient.invalidateQueries({ queryKey: ['customers'] });
            setNewlyCreatedKey(resData.licenseKey);
            setCreateDialogOpen(false);
            resetCreateForm();
            setSuccessSnackbarOpen(true);
        },
        onError: (err: any) => {
            showMessage(err.response?.data?.error || 'Failed to generate license');
        }
    });

    const updateMutation = useMutation({
        mutationFn: ({ licenseKey, data }: { licenseKey: string; data: any }) => updateLicense(licenseKey, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['licenses'] });
            queryClient.invalidateQueries({ queryKey: ['customers'] });
            setEditingLimitKey(null);
        },
    });

    const handleCreateLicense = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        
        const valError = getFeatureValidationError();
        if (valError) {
            showMessage(valError);
            return;
        }

        const features: Record<string, any> = {};
        newLicenseFeatures.forEach(flag => {
            if (flag.key.trim()) {
                const k = flag.key.trim();
                if (flag.type === 'boolean') {
                    features[k] = (flag.value === 'true');
                } else if (flag.type === 'number') {
                    const parsedNum = Number(flag.value);
                    features[k] = isNaN(parsedNum) ? 0 : parsedNum;
                } else {
                    features[k] = flag.value;
                }
            }
        });
        
        createMutation.mutate({
            productId: newProductId,
            customerId: newCustomerId || undefined,
            serverLimit: newServerLimit,
            gracePeriodDays: newGracePeriodDays,
            expiresAt: newExpiresAt ? new Date(newExpiresAt).getTime() : undefined,
            features: features,
        });
    };

    const handleUpdateStatus = async (licenseKey: string, status: 'active' | 'suspended' | 'revoked') => {
        try {
            await updateMutation.mutateAsync({ licenseKey, data: { status } });
            showMessage(`License ${status === 'active' ? 'reactivated' : status === 'suspended' ? 'suspended' : 'revoked'} successfully`);
            
            if (selectedLicense && selectedLicense.licenseKey === licenseKey) {
                const refreshed = licensesData?.licenses?.find((l: any) => l.licenseKey === licenseKey);
                if (refreshed) {
                    setSelectedLicense({ ...refreshed, status });
                }
            }
        } catch (error: any) {
            showMessage(error.message || 'Operation failed');
        }
    };

    const getLinkedServerCounters = (licenseKey: string) => {
        const linked = servers.filter((s: any) => s.licenseKey === licenseKey);
        return {
            active: linked.filter((s: any) => s.status === 'active').length,
            blocked: linked.filter((s: any) => s.status === 'blocked').length,
            suspicious: linked.filter((s: any) => s.status === 'suspicious').length,
            migrated: linked.filter((s: any) => s.status === 'migrated').length,
        };
    };

    const getTimelineLogStyle = (log: any) => {
        if (log.type === 'LICENSE_RENEWED') {
            return {
                bg: 'bg-green-500',
                text: 'text-green-700 dark:text-green-300',
                border: 'border-green-300 dark:border-green-700',
                icon: '🔄'
            };
        }
        if (log.type === 'LICENSE_UPDATE') {
            if (log.status === 'suspended') {
                return {
                    bg: 'bg-orange-500',
                    text: 'text-orange-700 dark:text-orange-300',
                    border: 'border-orange-300 dark:border-orange-700',
                    icon: '⏸️'
                };
            }
            if (log.status === 'revoked') {
                return {
                    bg: 'bg-red-500',
                    text: 'text-red-700 dark:text-red-300',
                    border: 'border-red-300 dark:border-red-700',
                    icon: '🚫'
                };
            }
            return {
                bg: 'bg-green-500',
                text: 'text-green-700 dark:text-green-300',
                border: 'border-green-300 dark:border-green-700',
                icon: '▶️'
            };
        }
        if (log.type === 'SERVER_BLOCK') {
            return {
                bg: 'bg-red-500',
                text: 'text-red-700 dark:text-red-300',
                border: 'border-red-300 dark:border-red-700',
                icon: '🔒'
            };
        }
        if (log.type === 'SERVER_MIGRATED') {
            return {
                bg: 'bg-blue-500',
                text: 'text-blue-700 dark:text-blue-300',
                border: 'border-blue-300 dark:border-blue-700',
                icon: '✈️'
            };
        }
        return {
            bg: 'bg-gray-400',
            text: 'text-gray-700 dark:text-gray-300',
            border: 'border-gray-300 dark:border-gray-700',
            icon: '📝'
        };
    };

    const handleStartEditLimit = (licenseKey: string, currentLimit: number) => {
        setEditingLimitKey(licenseKey);
        setTempLimit(currentLimit);
    };

    const handleSaveLimit = (licenseKey: string) => {
        updateMutation.mutate({ licenseKey, data: { serverLimit: tempLimit } });
    };

    const exportToCSV = () => {
        const list = licensesData?.licenses || [];
        if (list.length === 0) return;

        const headers = ['License Key', 'Product ID', 'Customer ID', 'Server Limit', 'Status', 'Expires At'];
        const rows = list.map((l: any) => [
            l.licenseKey,
            l.productId,
            l.customerId || 'None',
            l.serverLimit,
            l.status,
            l.expiresAt ? new Date(l.expiresAt).toLocaleDateString() : 'Never'
        ]);
        
        const csvContent = "data:text/csv;charset=utf-8," 
            + [headers.join(','), ...rows.map((e: any) => e.join(','))].join('\n');
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `licenses_report_${Date.now()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const products = productsData?.products || [];
    const customers = customersData?.customers || [];
    const licenses = licensesData?.licenses || [];

    const formatFeatureKey = (key: string) => {
        if (key.toLowerCase() === 'drm') return 'DRM';
        if (key.toLowerCase() === 'vod') return 'VOD';
        return key
            .split(/[_-]/)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    if (isLoadingLicenses) {
        return <div className="text-center py-8">Loading licenses...</div>;
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Licenses
                </h1>
                <div className="flex gap-2">
                    <button
                        onClick={() => {
                            resetCreateForm();
                            setCreateDialogOpen(true);
                        }}
                        className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md text-sm font-medium transition-colors"
                    >
                        Create License
                    </button>
                    <button
                        onClick={exportToCSV}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-medium transition-colors"
                    >
                        Export Report (CSV)
                    </button>
                </div>
            </div>

            {/* Create License Dialog */}
            <Dialog
                open={createDialogOpen}
                onClose={handleCloseCreateDialog}
                PaperProps={{ className: 'w-[480px] max-w-full !rounded-3xl dark:bg-gray-800 dark:text-white p-2' }}
            >
                <DialogTitle className="!font-bold !text-lg text-gray-900 dark:text-white">Create License</DialogTitle>
                <form onSubmit={handleCreateLicense}>
                    <DialogContent className="space-y-4 !py-2">
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">
                                Product
                            </label>
                            <select
                                value={newProductId}
                                onChange={(e) => handleProductChange(e.target.value)}
                                required
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            >
                                <option value="">Select Product</option>
                                {products.map((p: any) => (
                                    <option key={p.productId} value={p.productId}>{p.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">
                                Assign Customer (Optional)
                            </label>
                            <select
                                value={newCustomerId}
                                onChange={(e) => setNewCustomerId(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            >
                                <option value="">None</option>
                                {customers.map((c: any) => (
                                    <option key={c.customerId} value={c.customerId}>{c.companyName}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">
                                Expiration Date
                            </label>
                            <input
                                type="date"
                                value={newExpiresAt}
                                onChange={(e) => setNewExpiresAt(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">
                                    Server Limit
                                </label>
                                <input
                                    type="number"
                                    value={newServerLimit}
                                    onChange={(e) => setNewServerLimit(Math.max(1, parseInt(e.target.value) || 1))}
                                    min="1"
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">
                                    Grace Period (Days)
                                </label>
                                <input
                                    type="number"
                                    value={newGracePeriodDays}
                                    onChange={(e) => setNewGracePeriodDays(Math.max(0, parseInt(e.target.value) || 0))}
                                    min="0"
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                />
                            </div>
                        </div>

                        {/* Feature Flags Section */}
                        <div className="border-t pt-4 border-gray-200 dark:border-gray-700">
                            <div className="flex justify-between items-center mb-2">
                                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400">Feature Flags (Optional)</label>
                                <button
                                    type="button"
                                    onClick={() => setNewLicenseFeatures([...newLicenseFeatures, { key: '', type: 'boolean', value: 'true' }])}
                                    className="text-xs text-blue-500 dark:text-blue-400 hover:underline font-medium"
                                >
                                    + Add Feature Flag
                                </button>
                            </div>

                            {/* Inline Validation Error */}
                            {getFeatureValidationError() && (
                                <p className="text-[10px] text-red-500 font-semibold mb-2">{getFeatureValidationError()}</p>
                            )}

                            <div className="space-y-2 max-h-[140px] overflow-y-auto pr-1">
                                {newLicenseFeatures.length === 0 ? (
                                    <p className="text-[10px] text-gray-450 dark:text-gray-450 italic">No feature flags added.</p>
                                ) : (
                                    newLicenseFeatures.map((flag, idx) => (
                                        <div key={idx} className="flex gap-2 items-center">
                                            <input
                                                type="text"
                                                placeholder="Flag Key (e.g. drm)"
                                                value={flag.key}
                                                onChange={(e) => {
                                                    const updated = [...newLicenseFeatures];
                                                    updated[idx].key = e.target.value;
                                                    setNewLicenseFeatures(updated);
                                                }}
                                                required
                                                className="flex-grow px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono"
                                            />
                                            <select
                                                value={flag.type}
                                                onChange={(e) => {
                                                    const updated = [...newLicenseFeatures];
                                                    const newType = e.target.value as 'boolean' | 'string' | 'number';
                                                    updated[idx].type = newType;
                                                    updated[idx].value = newType === 'boolean' ? 'true' : newType === 'number' ? '0' : '';
                                                    setNewLicenseFeatures(updated);
                                                }}
                                                className="px-1.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            >
                                                <option value="boolean">Boolean</option>
                                                <option value="string">String</option>
                                                <option value="number">Number</option>
                                            </select>
                                            {flag.type === 'boolean' ? (
                                                <select
                                                    value={flag.value}
                                                    onChange={(e) => {
                                                        const updated = [...newLicenseFeatures];
                                                        updated[idx].value = e.target.value;
                                                        setNewLicenseFeatures(updated);
                                                    }}
                                                    className="px-1.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                >
                                                    <option value="true">True</option>
                                                    <option value="false">False</option>
                                                </select>
                                            ) : flag.type === 'number' ? (
                                                <input
                                                    type="number"
                                                    placeholder="0"
                                                    value={flag.value}
                                                    onChange={(e) => {
                                                        const updated = [...newLicenseFeatures];
                                                        updated[idx].value = e.target.value;
                                                        setNewLicenseFeatures(updated);
                                                    }}
                                                    required
                                                    className="w-20 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono"
                                                />
                                            ) : (
                                                <input
                                                    type="text"
                                                    placeholder="Value"
                                                    value={flag.value}
                                                    onChange={(e) => {
                                                        const updated = [...newLicenseFeatures];
                                                        updated[idx].value = e.target.value;
                                                        setNewLicenseFeatures(updated);
                                                    }}
                                                    required
                                                    className="w-20 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                />
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const updated = newLicenseFeatures.filter((_, i) => i !== idx);
                                                    setNewLicenseFeatures(updated);
                                                }}
                                                className="text-red-500 hover:text-red-700 font-bold px-1 text-sm"
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </DialogContent>
                    <DialogActions className="!px-6 !pb-4">
                        <Button onClick={handleCloseCreateDialog} className="!text-gray-500 hover:!bg-slate-100">Cancel</Button>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={createMutation.isPending || !!getFeatureValidationError()}
                            className="!rounded-lg !px-4"
                        >
                            {createMutation.isPending ? 'Creating...' : 'Create License'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Filters */}
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-sm border border-gray-150/40 dark:border-gray-700/60 p-4 mb-6 flex flex-wrap gap-4 items-center">
                <div className="flex-grow min-w-[200px]">
                    <input
                        placeholder="Search License Key..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                </div>
                <div>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                        <option value="">All Statuses</option>
                        <option value="active">Active</option>
                        <option value="suspended">Suspended</option>
                        <option value="revoked">Revoked</option>
                    </select>
                </div>
                <div>
                    <select
                        value={productFilter}
                        onChange={(e) => setProductFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                        <option value="">All Products</option>
                        {products.map((p: any) => (
                            <option key={p.productId} value={p.productId}>{p.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* List */}
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-sm border border-gray-150/40 dark:border-gray-700/60">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        License List
                    </h2>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead>
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                                    License Key
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                                    Product
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                                    Server Limit
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                                    Expires
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {licenses.map((license: any) => (
                                <tr 
                                    key={license.id}
                                    onClick={() => setSelectedLicense(license)}
                                    className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-all"
                                >
                                    <td className="px-6 py-4 text-sm font-mono text-gray-900 dark:text-gray-200 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <span>{license.licenseKey}</span>
                                            <CopyButton 
                                                text={license.licenseKey} 
                                                onCopy={() => showMessage('License key copied')} 
                                            />
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200 whitespace-nowrap font-medium">
                                        {license.productId}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200 whitespace-nowrap font-medium" onClick={(e) => e.stopPropagation()}>
                                        {editingLimitKey === license.licenseKey ? (
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="number"
                                                    value={tempLimit}
                                                    onChange={(e) => setTempLimit(parseInt(e.target.value) || 1)}
                                                    className="w-16 px-2 py-1 border border-gray-300 rounded dark:bg-gray-700 text-gray-900 dark:text-white"
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleSaveLimit(license.licenseKey);
                                                    }}
                                                    className="px-2 py-1 bg-blue-500 text-white rounded text-xs"
                                                >
                                                    Save
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-2">
                                                <span>{license.activeServerCount || 0} / {license.serverLimit} machines</span>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleStartEditLimit(license.licenseKey, license.serverLimit);
                                                    }}
                                                    className="text-xs text-blue-500 hover:underline whitespace-nowrap"
                                                >
                                                    Edit
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 py-1 text-xs rounded-full whitespace-nowrap ${license.status === 'active'
                                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                                : license.status === 'suspended'
                                                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                                    : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                            }`}>
                                            {license.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-950 dark:text-gray-200 whitespace-nowrap font-mono text-xs">
                                        {license.expiresAt ? new Date(license.expiresAt).toLocaleDateString() : 'Never'}
                                    </td>
                                    <td className="px-6 py-4 text-right text-sm space-x-2 whitespace-nowrap relative" onClick={(e) => e.stopPropagation()}>
                                        {license.status === 'active' ? (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setLicenseToSuspend(license);
                                                    setSuspendDialogOpen(true);
                                                }}
                                                className="px-2 py-1 bg-yellow-500 text-white text-xs rounded hover:bg-yellow-600 font-medium whitespace-nowrap inline-flex items-center justify-center align-middle"
                                            >
                                                Suspend
                                            </button>
                                        ) : license.status === 'suspended' ? (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleUpdateStatus(license.licenseKey, 'active');
                                                }}
                                                className="px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 font-medium whitespace-nowrap inline-flex items-center justify-center align-middle"
                                            >
                                                Reactivate
                                            </button>
                                        ) : null}

                                        {license.status !== 'revoked' && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setLicenseToRenew(license);
                                                    setRenewDialogOpen(true);
                                                }}
                                                className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 font-medium whitespace-nowrap inline-flex items-center justify-center align-middle"
                                            >
                                                Renew
                                            </button>
                                        )}

                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setOpenMenuLicenseKey(openMenuLicenseKey === license.licenseKey ? null : license.licenseKey);
                                            }}
                                            className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-slate-500 hover:text-slate-700 inline-flex items-center justify-center align-middle"
                                        >
                                            <MoreVertIcon className="!w-4 !h-4" />
                                        </button>

                                        {openMenuLicenseKey === license.licenseKey && (
                                            <div className="absolute right-6 mt-1 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-150 dark:border-gray-700 z-50 py-1 text-left">
                                                {license.status !== 'revoked' && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setLicenseToRevoke(license);
                                                            setRevokeDialogOpen(true);
                                                            setOpenMenuLicenseKey(null);
                                                        }}
                                                        className="block w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 font-medium text-left"
                                                    >
                                                        Revoke License
                                                    </button>
                                                )}
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedLicense(license);
                                                        setOpenMenuLicenseKey(null);
                                                    }}
                                                    className="block w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 text-left"
                                                >
                                                    View Details
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigator.clipboard.writeText(license.licenseKey);
                                                        showMessage('License key copied');
                                                        setOpenMenuLicenseKey(null);
                                                    }}
                                                    className="block w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 text-left"
                                                >
                                                    Copy License
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleStartEditLimit(license.licenseKey, license.serverLimit);
                                                        setOpenMenuLicenseKey(null);
                                                    }}
                                                    className="block w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 text-left"
                                                >
                                                    Edit Limits
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Shared License Details Drawer */}
            <LicenseDetailsDrawer
                open={!!selectedLicense}
                onClose={() => setSelectedLicense(null)}
                license={selectedLicense}
                onLicenseUpdated={() => {
                    queryClient.invalidateQueries({ queryKey: ['licenses'] });
                }}
            />

            {/* Suspend License Dialog Confirmation */}
            <Dialog
                open={suspendDialogOpen}
                onClose={() => setSuspendDialogOpen(false)}
            >
                <DialogTitle>Suspend License</DialogTitle>
                <DialogContent>
                    <DialogContentText className="!mb-2">
                        Are you sure you want to suspend this license?
                    </DialogContentText>
                    <DialogContentText>
                        All associated machines will stop validating until the license is resumed.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSuspendDialogOpen(false)}>Cancel</Button>
                    <Button 
                        onClick={() => {
                            if (licenseToSuspend) {
                                handleUpdateStatus(licenseToSuspend.licenseKey, 'suspended');
                            }
                            setSuspendDialogOpen(false);
                        }} 
                        color="warning" 
                        autoFocus
                    >
                        Suspend License
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Revoke License Dialog Confirmation */}
            <Dialog
                open={revokeDialogOpen}
                onClose={() => setRevokeDialogOpen(false)}
            >
                <DialogTitle className="!text-red-650 dark:!text-red-400 font-bold">⚠️ Revoke License</DialogTitle>
                <DialogContent>
                    <DialogContentText className="!mb-3 !text-gray-900 dark:!text-gray-100 font-medium">
                        Are you sure you want to revoke this license?
                    </DialogContentText>
                    <DialogContentText className="!text-red-500 font-semibold">
                        This action cannot be easily undone and all associated machines will permanently stop validating.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRevokeDialogOpen(false)}>Cancel</Button>
                    <Button 
                        onClick={() => {
                            if (licenseToRevoke) {
                                handleUpdateStatus(licenseToRevoke.licenseKey, 'revoked');
                            }
                            setRevokeDialogOpen(false);
                        }} 
                        className="!bg-red-600 !text-white hover:!bg-red-700"
                        autoFocus
                    >
                        Revoke License
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Renew License Dialog */}
            <RenewLicenseDialog
                open={renewDialogOpen}
                onClose={() => setRenewDialogOpen(false)}
                license={licenseToRenew}
                onSuccess={() => {
                    queryClient.invalidateQueries({ queryKey: ['licenses'] });
                    queryClient.invalidateQueries({ queryKey: ['logs'] });
                    queryClient.invalidateQueries({ queryKey: ['renewals'] });
                    showMessage('License renewed successfully');
                    setRenewDialogOpen(false);
                    
                    // Refresh details drawer if open
                    if (selectedLicense) {
                        const refreshed = licensesData?.licenses?.find((l: any) => l.licenseKey === selectedLicense.licenseKey);
                        if (refreshed) setSelectedLicense(refreshed);
                    }
                }}
            />

            {/* Snackbars */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={2000}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarMessage}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            />

            {/* Success Snackbar with Copy Button */}
            <Snackbar
                open={successSnackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSuccessSnackbarOpen(false)}
                message={`License created successfully: ${newlyCreatedKey}`}
                action={
                    <Button
                        color="secondary"
                        size="small"
                        onClick={() => {
                            navigator.clipboard.writeText(newlyCreatedKey);
                            showMessage('License key copied to clipboard');
                            setSuccessSnackbarOpen(false);
                        }}
                    >
                        Copy Key
                    </Button>
                }
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            />
        </div>
    );
}